// frontend/src/components/GestoraExportDialog.jsx
import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { apiFetch, API } from "@/utils/api";

const fmt = (n, currency = "EUR") =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency }).format(n || 0);

// Modal de selección previo a generar el Excel de la gestora. Lista las facturas
// candidatas del trimestre (pendientes + arrastradas), todas marcadas; el usuario
// desmarca las ya presentadas. "Generar Excel" no sella; "Dar por presentado" sí.
// `onOpenDraft(year, quarter)` (opcional): abre el borrador informativo del 303
// del periodo (roadmap 2.5 bloque 4) — el cierre y el borrador CONVIVEN.
export default function GestoraExportDialog({ open, onOpenChange, year, quarter, onOpenDraft }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [included, setIncluded] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    setData(null);
    apiFetch(`${API}/fiscal/candidates?year=${year}&quarter=${quarter}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        setData(d);
        const all = [...d.emitted, ...d.received].map((i) => i.invoice_id);
        setIncluded(new Set(all));
      })
      .catch(() => toast.error("No se pudieron cargar las facturas del trimestre"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) load();
  }, [open, year, quarter]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) =>
    setIncluded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const includeIds = () => Array.from(included);
  const allIds = () =>
    data ? [...data.emitted, ...data.received].map((i) => i.invoice_id) : [];

  // Rango del trimestre para el atajo "Marcar solo este trimestre". Las candidatas
  // pueden incluir facturas arrastradas de trimestres anteriores sin presentar
  // (fecha <= fin del trimestre pero de un periodo previo); el atajo deja marcadas
  // solo las de ESTE trimestre.
  const qStart = `${year}-${String(quarter * 3 - 2).padStart(2, "0")}-01`;
  const qLastDay = new Date(year, quarter * 3, 0).getDate();
  const qEnd = `${year}-${String(quarter * 3).padStart(2, "0")}-${String(qLastDay).padStart(2, "0")}`;
  const selectQuarterOnly = () => {
    if (!data) return;
    const inQuarter = [...data.emitted, ...data.received]
      .filter((i) => {
        const d = i.invoice_date?.slice(0, 10);
        return d && d >= qStart && d <= qEnd;
      })
      .map((i) => i.invoice_id);
    setIncluded(new Set(inQuarter));
  };
  const selectAll = () => setIncluded(new Set(allIds()));

  const generate = async () => {
    setBusy(true);
    try {
      const res = await apiFetch(`${API}/export/gestora-excel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ year, quarter, include_ids: includeIds() }),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cierre_${year}_T${quarter}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel generado");
    } catch {
      toast.error("Error al generar el Excel");
    } finally {
      setBusy(false);
    }
  };

  // Sellar = TODAS las candidatas, aunque estén desmarcadas del Excel (con el
  // corte de trimestre el backend ya solo lista facturas con fecha <= fin del
  // trimestre; las posteriores van al cierre siguiente).
  const sealIds = () => allIds();

  const markFiled = async () => {
    const ids = sealIds();
    if (!window.confirm(
      `Se marcarán como presentadas TODAS las facturas pendientes hasta el T${quarter} ${year} ` +
      `(incluidas las que hayas desmarcado del Excel): ${ids.length} en total. El "IVA a ` +
      `apartar" del Dashboard se recalculará. ¿Continuar?`
    ))
      return;
    setBusy(true);
    try {
      const res = await apiFetch(`${API}/fiscal/mark-filed`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ year, quarter, invoice_ids: ids }),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      toast.success(`${d.marked} facturas marcadas como presentadas`);
      onOpenChange(false);
    } catch {
      toast.error("Error al marcar como presentadas");
    } finally {
      setBusy(false);
    }
  };

  // Deshacer un "Dar por presentado" pulsado por error: las facturas selladas en
  // este periodo vuelven a pendientes (2 incidentes reales el 2026-07-04).
  const undoFiled = async () => {
    if (!window.confirm(
      `Se deshará el "presentado" del T${quarter} ${year}: todas las facturas selladas en ese ` +
      `periodo volverán a estar pendientes y reaparecerán en esta lista. ¿Continuar?`
    ))
      return;
    setBusy(true);
    try {
      const res = await apiFetch(`${API}/fiscal/unmark`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ year, quarter }),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      toast.success(`${d.unmarked} facturas vuelven a estar pendientes`);
      load();
    } catch {
      toast.error("Error al deshacer el presentado");
    } finally {
      setBusy(false);
    }
  };

  // El periodo con facturas selladas ofrece SOLO deshacer; sin ellas, SOLO dar
  // por presentado (hallazgo smoke 2026-07-11: ambos botones a la vez confunden).
  const alreadyFiled = (data?.filed_count ?? 0) > 0;

  const Row = ({ inv, name }) => {
    const currency = inv.currency ?? "EUR";
    const foreign = currency !== "EUR";
    return (
      <label className="flex items-center gap-2 py-1 text-sm border-b border-slate-100 cursor-pointer">
        <input
          type="checkbox"
          checked={included.has(inv.invoice_id)}
          onChange={() => toggle(inv.invoice_id)}
        />
        <span className="flex-1 truncate">{name} · {inv.invoice_number}</span>
        <span className="text-slate-500">{inv.invoice_date?.slice(0, 10)}</span>
        {foreign && (
          <span className="text-xs text-slate-400 whitespace-nowrap">
            {inv.total_amount_eur != null ? `= ${fmt(inv.total_amount_eur)}` : "EUR pendiente"}
          </span>
        )}
        <span className="font-mono w-24 text-right">{fmt(inv.total_amount, currency)}</span>
      </label>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Cierre de trimestre · {year} T{quarter}</DialogTitle>
          <p className="text-sm text-slate-500">
            Export: el Excel con las facturas del trimestre agrupadas para
            presentar los impuestos (tú o tu gestoría/asesoría).
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10 text-slate-400">
            <RefreshCw className="w-6 h-6 animate-spin" />
          </div>
        ) : !data ? null : (
          <>
            <p className="text-sm text-slate-500">
              Se listan <strong>todas las facturas pendientes</strong> hasta el fin del
              trimestre (arrastradas de trimestres anteriores incluidas); las fechadas
              después del trimestre no aparecen: van al siguiente. Las casillas controlan
              qué entra en el <strong>Excel</strong> (desmarca lo que ya presentaste en un
              modelo anterior). Al pulsar <strong>"Dar por presentado"</strong> se sellan
              todas las pendientes hasta este trimestre, aunque estén desmarcadas del
              Excel. IVA a apartar de lo pendiente:{" "}
              <strong>{fmt(data.iva_to_reserve)}</strong>.
            </p>
            {data.draft_excluded_count > 0 && (
              <div
                className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2 text-sm"
                data-testid="draft-excluded-warning"
              >
                {data.draft_excluded_count === 1
                  ? "Hay 1 factura en borrador que queda fuera de este cierre"
                  : `Hay ${data.draft_excluded_count} facturas en borrador que quedan fuera de este cierre`}
                : un borrador no está emitido en contabilidad. Si debe entrar,
                ve a Facturas emitidas y usa "Emitir factura" antes de generar
                el Excel.
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={selectQuarterOnly}>
                Marcar solo T{quarter} {year}
              </Button>
              <Button variant="ghost" size="sm" onClick={selectAll}>
                Marcar todas (arrastradas incluidas)
              </Button>
            </div>
            <div className="max-h-[50vh] overflow-y-auto space-y-4 py-2">
              <div>
                <h4 className="font-semibold text-sm mb-1">Ventas ({data.emitted.length})</h4>
                {data.emitted.map((e) => (
                  <Row key={e.invoice_id} inv={e} name={e.client_name} />
                ))}
              </div>
              <div>
                <h4 className="font-semibold text-sm mb-1">Compras ({data.received.length})</h4>
                {data.received.map((r) => (
                  <Row key={r.invoice_id} inv={r} name={r.supplier_name} />
                ))}
              </div>
            </div>
            <div className="flex flex-wrap justify-between gap-2 pt-2">
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={generate} disabled={busy}>
                  Generar Excel
                </Button>
                {onOpenDraft && (
                  <Button variant="ghost" size="sm" className="text-teal-700 self-center"
                          data-testid="gestora-open-draft"
                          onClick={() => { onOpenChange(false); onOpenDraft(year, quarter); }}>
                    Ver borrador 303 (informativo)
                  </Button>
                )}
              </div>
              {alreadyFiled ? (
                <Button variant="outline" onClick={undoFiled} disabled={busy}>
                  Deshacer "presentado" T{quarter}
                </Button>
              ) : (
                <Button onClick={markFiled} disabled={busy}>
                  Dar por presentado el 303 T{quarter}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
